import PropTypes from "prop-types";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Close as CloseIcon, InfoOutlined } from "@mui/icons-material";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { formatDate, formatMoney } from "@/features/shared";

import { statusColors, statusLabels } from "../../status";

export const GeneralInformationSection = ({ salesInvoice }) => {
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  const hasPayments =
    salesInvoice.salesPayments && salesInvoice.salesPayments.length > 0;

  const returnedAmount = salesInvoice.returnedAmount || 0;
  const totalDownPayment = (salesInvoice.salesDownPaymentAllocations || []).reduce(
    (sum, allocation) => sum + (Number(allocation.amount) || 0),
    0,
  );

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1">Informasi Umum</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Status
          </Typography>
          {salesInvoice.status && (
            <Chip
              label={statusLabels[salesInvoice.status]}
              color={statusColors[salesInvoice.status]}
              size="small"
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Dibuat oleh
          </Typography>
          <Typography variant="body1">
            {salesInvoice.creator?.name || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Tanggal Transaksi
          </Typography>
          <Typography variant="body1">
            {formatDate(salesInvoice.transactionDate)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Pelanggan
          </Typography>
          <Typography variant="body1">
            {salesInvoice.buyer?.name || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Outlet
          </Typography>
          <Typography variant="body1">
            {salesInvoice.seller?.name || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            No. Faktur Pelanggan
          </Typography>
          <Typography variant="body1">
            {salesInvoice.buyerReferenceNumber || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Termin Pembayaran
          </Typography>
          <Typography variant="body1">
            {salesInvoice.paymentTerm?.description ||
              salesInvoice.paymentTerm?.name ||
              "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Subtotal
          </Typography>
          <Typography variant="body1">
            {formatMoney(salesInvoice.subtotal)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Pajak
          </Typography>
          <Typography variant="body1">
            {formatMoney(salesInvoice.taxAmount)} ({salesInvoice.taxPercentage}
            %)
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Total Diskon
          </Typography>
          <Typography variant="body1">
            {formatMoney(salesInvoice.totalDiscount)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Grand Total
          </Typography>
          <Typography variant="body1" sx={{
            fontWeight: "bold"
          }}>
            {formatMoney(salesInvoice.grandTotal)}
          </Typography>
        </Grid>
        {totalDownPayment > 0 && (
          <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Uang Muka
            </Typography>
            <Typography variant="body1">
              {formatMoney(totalDownPayment)}
            </Typography>
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Terutang
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{
            alignItems: "center"
          }}>
            <Typography
              variant="body1"
              color={
                salesInvoice.outstandingAmount > 0
                  ? "error.main"
                  : "success.main"
              }
              sx={{
                fontWeight: "bold"
              }}
            >
              {formatMoney(salesInvoice.outstandingAmount)}
            </Typography>
            {hasPayments && (
              <IconButton
                size="small"
                onClick={() => setPaymentDialogOpen(true)}
                sx={{ color: "primary.main" }}
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Catatan
          </Typography>
          <Typography variant="body1">{salesInvoice.note ?? "-"}</Typography>
        </Grid>
      </Grid>
      {/* Payment Details Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={2} sx={{
            alignItems: "center"
          }}>
            <Typography variant="h6">Detail Pembayaran</Typography>
            <Chip
              label={`${salesInvoice.salesPayments?.length || 0} Transaksi`}
              size="small"
              color="primary"
            />
          </Stack>
          <IconButton onClick={() => setPaymentDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Box>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Grand Total
                </Typography>
                <Typography variant="body1">
                  {formatMoney(salesInvoice.grandTotal)}
                </Typography>
              </Stack>
            </Box>

            {totalDownPayment > 0 && (
              <Box>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    Uang Muka
                  </Typography>
                  <Typography variant="body1">
                    -{formatMoney(totalDownPayment)}
                  </Typography>
                </Stack>
              </Box>
            )}

            {returnedAmount > 0 && (
              <Box>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    Total Retur
                  </Typography>
                  <Typography variant="body1">
                    -{formatMoney(returnedAmount)}
                  </Typography>
                </Stack>
              </Box>
            )}

            {salesInvoice.salesPayments?.map((payment) => (
              <Box key={payment.id}>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                  <Typography
                    component={Link}
                    to={`/sales/sales-payments/${payment.referenceNumber}`}
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {payment.referenceNumber}
                  </Typography>
                  <Typography variant="body1">
                    -{formatMoney(payment.paidAmount)}
                  </Typography>
                </Stack>
              </Box>
            ))}

            <Box>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Sisa Terutang
                </Typography>
                <Typography
                  variant="body1"
                  color={
                    salesInvoice.outstandingAmount > 0
                      ? "error.main"
                      : "success.main"
                  }
                  sx={{
                    fontWeight: "bold"
                  }}
                >
                  {formatMoney(salesInvoice.outstandingAmount)}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  salesInvoice: PropTypes.object.isRequired,
};
